/**
 * Generation des miniatures pendant les temps morts du navigateur.
 *
 * Composer un tableau coute quelques millisecondes ; en generer 9 a 30 d'un coup
 * au montage de la galerie ferait sauter des frames pile au moment ou le podium
 * s'anime. On passe donc par requestIdleCallback, et une seule miniature est
 * calculee a la fois : la file est globale, pas par composant.
 */
import { useEffect, useState } from 'react';
import { renderThumbnail, type ArtworkRenderInput } from './renderArtwork.js';

type IdleHandle = number;

interface IdleWindow {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => IdleHandle;
  cancelIdleCallback?: (handle: IdleHandle) => void;
}

const idleWindow = window as unknown as IdleWindow;

/** Repli pour Safari, qui n'expose pas encore requestIdleCallback partout. */
function requestIdle(callback: () => void): IdleHandle {
  if (typeof idleWindow.requestIdleCallback === 'function') {
    // Le timeout garantit que la file avance meme si le navigateur reste occupe.
    return idleWindow.requestIdleCallback(callback, { timeout: 1200 });
  }
  return window.setTimeout(callback, 32);
}

function cancelIdle(handle: IdleHandle): void {
  if (typeof idleWindow.cancelIdleCallback === 'function') idleWindow.cancelIdleCallback(handle);
  else window.clearTimeout(handle);
}

// File globale : une miniature a la fois, quel que soit le nombre de cartes.
const queue: Array<() => Promise<void>> = [];
let running = false;
let handle: IdleHandle | null = null;

function pump(): void {
  if (running || queue.length === 0) return;
  running = true;
  handle = requestIdle(() => {
    handle = null;
    const job = queue.shift();
    if (job === undefined) {
      running = false;
      return;
    }
    void job().finally(() => {
      running = false;
      pump();
    });
  });
}

function enqueue(job: () => Promise<void>): () => void {
  queue.push(job);
  pump();
  return () => {
    const index = queue.indexOf(job);
    if (index >= 0) queue.splice(index, 1);
    if (queue.length === 0 && handle !== null) {
      cancelIdle(handle);
      handle = null;
      running = false;
    }
  };
}

export type ThumbnailState =
  | { readonly status: 'pending' }
  | { readonly status: 'ready'; readonly dataUrl: string }
  | { readonly status: 'error' };

/**
 * Miniature d'un tableau retouche. Reste en 'pending' tant que le blob n'est
 * pas arrive : c'est l'etat de chargement par item.
 */
export function useIdleThumbnail(
  input: ArtworkRenderInput | null,
  size: number,
): ThumbnailState {
  const [state, setState] = useState<ThumbnailState>({ status: 'pending' });

  useEffect(() => {
    if (input === null) {
      setState({ status: 'pending' });
      return;
    }

    let alive = true;
    setState({ status: 'pending' });

    const cancel = enqueue(async () => {
      if (!alive) return;
      try {
        const dataUrl = await renderThumbnail(input, size);
        if (alive) setState({ status: 'ready', dataUrl });
      } catch {
        if (alive) setState({ status: 'error' });
      }
    });

    return () => {
      alive = false;
      cancel();
    };
  }, [input, size]);

  return state;
}
